import { Button } from "@/components/ui/button";
import { Compass, ListChecks, MessagesSquare, ArrowRight, ShieldCheck, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ROLES } from "@/lib/roles";
import { handleGoogleLogin } from "@/lib/googleLogin";

const HERO = "https://static.prod-images.emergentagent.com/jobs/924754d3-150a-4aa7-9c22-89eb688020c4/images/21011869008aa2493e51f0684d38feac73ebe0017ae9dd4ee13bb52a84312176.jpeg";
const PUEBLO = "https://static.prod-images.emergentagent.com/jobs/924754d3-150a-4aa7-9c22-89eb688020c4/images/9b6fbb173f0ad611b5b2cd1260f4065ce43379c00bab79dc40928ba312cf53b3.jpeg";

function handleLogin(path = "/") {
  handleGoogleLogin(path);
}

const STEPS = [
  {
    icon: Compass,
    title: "Crea tu proyecto",
    text: "Nombre, municipio, presupuesto estimado y una breve descripción de la casa que imaginas.",
  },
  {
    icon: ListChecks,
    title: "Sigue la hoja de ruta",
    text: "9 fases claras, desde el terreno hasta la entrada a vivir, con tareas y progreso visible.",
  },
  {
    icon: MessagesSquare,
    title: "Pregunta a Mori",
    text: "Un asistente que resuelve tus dudas del proceso en lenguaje claro, siempre que lo necesites.",
  },
];

export default function Welcome({ redirectPath = "/" }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="welcome-page">
      {/* Header */}
      <header className="border-b border-border/70 bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/mori-logo.jpg" alt="Mori" className="h-9 w-9 rounded-lg object-cover" />
            <span className="text-xl font-bold tracking-tight lowercase">mori</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              data-testid="header-password-login-button"
              variant="ghost"
              onClick={() => navigate("/entrar")}
              className="rounded-full px-4 text-muted-foreground hover:text-foreground"
            >
              Iniciar sesión
            </Button>
            <Button data-testid="login-google-button" onClick={() => handleLogin(redirectPath)} className="rounded-full px-5 !bg-[#C75B28] hover:!bg-[#A5461E] !text-[#F8F5EE]">
              Entrar con Google
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <span className="caption inline-flex items-center gap-2 mb-5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#C75B28]" />
            Autopromoción en España
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08]">
            Construye tu casa con <span className="text-[#C75B28]">pasos claros</span>, sin perderte.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
            Mori acompaña a quien construye su propia vivienda unifamiliar: una hoja de ruta por fases,
            tus tareas organizadas y un asistente que resuelve tus dudas del proceso. Pensado para
            personas sin experiencia técnica.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="hero-login-button"
              onClick={() => handleLogin(redirectPath)}
              size="lg"
              className="rounded-full px-7 h-12 text-base group !bg-[#C75B28] hover:!bg-[#A5461E] !text-[#F8F5EE]"
            >
              Empezar mi proyecto
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5" />
            Entras con tu cuenta de Google. Tus datos son solo tuyos.
          </p>
          <button
            type="button"
            data-testid="hero-password-login-link"
            onClick={() => navigate("/entrar")}
            className="mt-2 text-sm text-primary hover:underline inline-flex items-center gap-1.5"
          >
            <Mail className="h-3.5 w-3.5" />
            ¿Te han invitado? Inicia sesión con tu email y contraseña
          </button>
        </div>

        <div className="relative animate-fade-up" style={{ animationDelay: "0.12s" }}>
          <div className="absolute -inset-3 seigaiha-pattern rounded-3xl opacity-70" />
          <img
            src={HERO}
            alt="Vivienda unifamiliar en estilo ukiyo-e de Hokusai"
            className="relative rounded-2xl shadow-2xl object-cover w-full h-[360px] sm:h-[460px] border-4 border-[#F8F5EE] ring-1 ring-[#C75B28]/20"
          />
          <div className="absolute -bottom-3 -left-3 bg-[#C75B28] text-[#F8F5EE] text-[10px] uppercase tracking-[0.18em] font-mono px-3 py-1.5 rounded-full shadow-lg">
            Estilo Hokusai
          </div>
        </div>
      </section>

      {/* Portales */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="portals-section">
        <div className="caption mb-3">Accesos</div>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Entra por tu portal</h2>
        <p className="text-sm text-muted-foreground max-w-2xl mb-8">
          Cada proyecto tiene tres accesos diferenciados. Elige el tuyo e identifícate con Google: te llevaremos directamente a tu espacio.
        </p>
        <div className="relative mb-10 rounded-2xl overflow-hidden border-4 border-[#F8F5EE] ring-1 ring-[#C75B28]/20 shadow-xl">
          <img src={PUEBLO} alt="Pueblo español en estilo ukiyo-e de Hokusai" className="w-full h-40 sm:h-56 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#6E2E12]/70 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-5 text-[#F8F5EE]">
            <div className="text-xs uppercase tracking-[0.18em] font-mono opacity-90">Del terreno a la entrada a vivir</div>
            <div className="text-lg font-semibold">Acompañamos cada fase de tu autopromoción</div>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {Object.values(ROLES).map((r, i) => (
            <div
              key={r.key}
              data-testid={`portal-card-${r.key}`}
              className={`rounded-2xl border p-7 flex flex-col animate-fade-up transition-shadow hover:shadow-lg ${
                redirectPath === r.path ? "border-[#C75B28] bg-[#C75B28]/[0.06]" : "border-border bg-card"
              }`}
              style={{ animationDelay: `${0.08 * i}s` }}
            >
              <div className="h-11 w-11 rounded-xl bg-[#C75B28]/10 text-[#C75B28] flex items-center justify-center mb-5">
                <r.icon className="h-5 w-5" />
              </div>
              <h3 className="text-xl font-semibold mb-1.5">{r.label}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1">{r.description}</p>
              <Button
                data-testid={`portal-login-${r.key}`}
                variant={redirectPath === r.path ? "default" : "outline"}
                className="rounded-full mt-6 w-full"
                onClick={() => handleLogin(r.path)}
              >
                Entrar como {r.label.toLowerCase()}
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-10">Cómo funciona</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div
              key={s.title}
              className="rounded-2xl border border-border bg-card p-7 hover:shadow-lg transition-shadow animate-fade-up"
              style={{ animationDelay: `${0.1 * i}s` }}
              data-testid={`step-card-${i}`}
            >
              <div className="h-11 w-11 rounded-xl bg-accent text-olive flex items-center justify-center mb-5">
                <s.icon className="h-5 w-5" />
              </div>
              <div className="caption mb-2">Paso {i + 1}</div>
              <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border/70 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-2">
          <span>mori · asistente para autopromotores</span>
          <span>Guía orientativa, no asesoramiento legal, técnico ni financiero vinculante.</span>
        </div>
      </footer>
    </div>
  );
}
